// Shared kmb-stt client (round-15, 2026-08-31). ONE app-wide WebSocket to the device-local
// speech service (ws://127.0.0.1:9224 — same box as the kiosk Chromium, see
// apps/side/pingpong/kmb-stt/), gated by the Settings "Voice commands" toggle. DebugStt.vue keeps
// its own connection (it's a raw tuning surface and also sends config frames); this store is the
// PRODUCT consumer: LiveMatch subscribes to classified commands, and every finalized utterance
// is forwarded to the server's voice corpus (POST /api/voice/samples) — labeled with a playerId
// while an enrollment session ("Train my voice", Player.vue) is active, unlabeled/throttled
// otherwise.
//
// Speaker attribution: kmb-stt attaches the Vosk x-vector ("spk") + utterance audio to FINAL
// events, not to command events (they come from the separate grammar recognizer). A command and
// its free-decode final describe the same utterance and land within a beat of each other, so we
// keep the most recent final and pair a command with it when the command needs a speaker
// ("gano yo" / "punto mío") — see lastFinal + CLAIM_PAIR_WINDOW_MS.
import { reactive, watch } from 'vue'
import { settings } from './store/settings'
import { postVoiceSample } from './backend'
import { shipEvent } from './logging'
// NOT a static import: semanticIntent.ts pulls in @xenova/transformers (onnxruntime-web's WASM
// runtime) — a large dependency that must never inflate/slow-parse the app's main bundle just
// because voice commands exist. Every call site below uses a dynamic import() instead, which
// Vite code-splits into its own chunk fetched only when actually needed. See that module's
// docblock for the full "lazy + non-blocking" rationale.

export type SttLang = 'es' | 'en'

export interface SttCommandEvent {
  type: 'command'
  command: 'start' | 'end' | 'score' | 'win_claim' | 'point_claim' | 'nav_new_match' | 'corner_claim' | 'yes_no'
  raw: string
  lang: SttLang
  confidence: number
  score: { left: number; right: number } | null
  claimant?: 'me' | 'you'
  color?: 'red' | 'blue'
  // Round-27 (2026-08-31): set only on 'yes_no' commands (kmb_stt.py's yes/no grammar, added for
  // the corner-claim voice-confirmation flow — "¿Eres Luuk?"). kmb-stt classifies this whenever
  // it hears a bare sí/no (or "soy yo"/"no soy yo") REGARDLESS of app state — it has no notion of
  // a pending confirmation. Consumers must only ACT on it while services/confirmFlow.ts's state
  // machine is actually awaiting an answer; see App.vue's onSttCommand hub.
  answer?: 'yes' | 'no'
  ts: number
  // Round-24 (2026-08-31): which path produced this command — 'grammar' (kmb_stt.py's
  // classify_command, the default/primary path, omitted here for wire-compatibility with
  // existing server events which never set it) or 'semantic' (semanticIntent.ts's Tier-2
  // embedding fallback, client-side only, never sent by the server). Every consumer that
  // branches on `command`/`color` already works unchanged either way; this field exists purely
  // so logging/UI CAN distinguish "the table understood the exact words" from "the table guessed
  // from a similar phrasing" if it ever wants to.
  source?: 'semantic'
}

export interface SttFinalEvent {
  type: 'final'
  text: string
  lang: SttLang
  spk?: number[]
  // Round-16 (2026-08-31): which spk model produced `spk`'s embedding space — see
  // kmb_stt.py's SPK_MODEL_VERSION. Absent on an spk-less final; matching code must never
  // compare vectors stamped with two different values.
  spkModelVersion?: string
  // Round-20 (2026-08-31): utterance length, from kmb_stt.py's word timings (last word `end`
  // minus first word `start`). Feeds voiceScore.ts's speakerConfidence() duration-quality
  // factor — null on a legacy kmb-stt that predates this field, or a result with no word
  // timings, treated conservatively (the floor quality) by durationQuality().
  durationSec?: number | null
  audioB64?: string
  ts: number
}

interface SttState {
  connected: boolean
  /** Most recent free-decode final — carries the speaker fingerprint for the same utterance. */
  lastFinal: SttFinalEvent | null
  /** Round-19 (2026-08-31): most recent classified command — the Home mic popover's "last
   *  command" line, so voice detection can be sanity-checked without opening the Debug panel. */
  lastCommand: SttCommandEvent | null
  /** Round-19: latest kmb-stt `level` (rms) reading — drives the Home mic icon's pulse and the
   *  popover's VU bar, same rms the Debug panel's meter already scales by ×300. */
  level: number
  /** Enrollment target: set while a Player.vue "Train my voice" session is running. */
  enrollPlayerId: number | null
  enrollCount: number
  // Item 1 (round-18, 2026-08-31): the language the trainee is expected to be reading in.
  // kmb-stt runs es+en recognizers in parallel ("both" mode) and decodes the SAME utterance
  // through both — during enrollment only the expected-lang decode is the trainee's actual
  // speech; the other language's final for that utterance is a parallel-decode artifact
  // (garbage transcription of Spanish forced through the English model, or vice versa), never
  // a second thing they said. forwardSample() below refuses to post/count a final whose
  // lang doesn't match this while enrolling.
  enrollLang: SttLang | null
}

export const stt = reactive<SttState>({
  connected: false,
  lastFinal: null,
  lastCommand: null,
  level: 0,
  enrollPlayerId: null,
  enrollCount: 0,
  enrollLang: null,
})

/** How close (ms) a final must be to a claim command to count as the same utterance. */
export const CLAIM_PAIR_WINDOW_MS = 4000

type CommandListener = (ev: SttCommandEvent) => void
const commandListeners = new Set<CommandListener>()

/** Subscribe to classified voice commands. Returns an unsubscribe function. */
export function onSttCommand(fn: CommandListener): () => void {
  commandListeners.add(fn)
  return () => commandListeners.delete(fn)
}

/** The fingerprint (+ its spk model stamp) of the utterance a just-received command belongs to,
 *  if fresh enough. */
export function spkForCommand(cmd: SttCommandEvent): { spk: number[]; spkModelVersion?: string; durationSec?: number | null } | null {
  const f = stt.lastFinal
  if (!f?.spk) return null
  if (Math.abs(cmd.ts - f.ts) * 1000 > CLAIM_PAIR_WINDOW_MS && Date.now() - f.ts * 1000 > CLAIM_PAIR_WINDOW_MS) return null
  return { spk: f.spk, spkModelVersion: f.spkModelVersion, durationSec: f.durationSec }
}

// --------------------------------------------------------------------------------------------
// App-wide transcript log (round-25, 2026-08-31) — a rolling history of everything the shared
// kmb-stt client has heard, timestamped, so DebugStt.vue's transcript pane shows what happened
// on OTHER screens too (Home included), not just whatever arrived while the Debug tab itself
// was open. This is a local debugging convenience ONLY, on top of (never instead of) shipEvent's
// track-everything Loki stream — everything that already ships still ships unchanged.
//
// Persisted to localStorage so a reload doesn't wipe recent history (the kiosk page gets
// reloaded on every deploy). Kiosk-shared caveat: localStorage is per-BROWSER-ORIGIN, not
// per-tab — if this kiosk ever ran more than one browser window/tab against the same origin
// they'd read/write the same buffer. Today's kiosk is a single wall-mounted Chromium tab, so
// that's a non-issue; worth remembering if that ever changes.
// --------------------------------------------------------------------------------------------
export interface SttTranscriptEntry {
  id: number
  /** Epoch ms, client wall-clock at the moment this entry was last recorded/updated. */
  ts: number
  /** Local HH:MM:SS, precomputed so the UI never needs a date lib. */
  time: string
  lang: SttLang
  text: string
  /** 'partial' while still being updated in place; becomes 'final' once kmb-stt finalizes the
   *  utterance — see recordPartial/recordFinal below. */
  kind: 'partial' | 'final'
  durationSec?: number | null
  command?: {
    command: SttCommandEvent['command']
    color?: 'red' | 'blue'
    source: 'grammar' | 'semantic'
    confidence: number
  } | null
  speakerMatch?: {
    playerId: number | null
    displayName?: string | null
    confidencePct: number | null
    outcome: string
    // Round-28 (2026-08-31): which candidate-scope ladder tier (services/candidateScope.ts) was
    // applied to this match attempt, e.g. "match players (2)" — makes "why didn't it recognize
    // the other guy" debuggable straight from the transcript pane instead of re-deriving it from
    // whichever screen/context produced the command.
    scope?: string | null
  } | null
}

const TRANSCRIPT_MAX = 200
const TRANSCRIPT_STORAGE_KEY = 'pingpong.stt.transcriptLog.v1'

function loadTranscriptLog(): SttTranscriptEntry[] {
  try {
    const raw = localStorage.getItem(TRANSCRIPT_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return (parsed as SttTranscriptEntry[]).slice(-TRANSCRIPT_MAX)
  } catch {
    return []
  }
}

/** Read by DebugStt.vue's transcript pane. Fed by handleMessage below regardless of which
 *  screen is currently mounted — this is the one app-wide kmb-stt client (see module docblock). */
export const sttTranscriptLog = reactive<SttTranscriptEntry[]>(loadTranscriptLog())

let transcriptId = sttTranscriptLog.length ? Math.max(...sttTranscriptLog.map((e) => e.id)) : 0
let persistTimer: ReturnType<typeof setTimeout> | null = null

function persistTranscriptLog() {
  // Debounced — partials can update several times a second while someone's mid-sentence.
  if (persistTimer) return
  persistTimer = setTimeout(() => {
    persistTimer = null
    try {
      localStorage.setItem(TRANSCRIPT_STORAGE_KEY, JSON.stringify(sttTranscriptLog))
    } catch {
      // storage full/unavailable — the in-memory log still works for this page load
    }
  }, 500)
}

/** DebugStt.vue's "Clear" button. */
export function clearTranscriptLog() {
  sttTranscriptLog.splice(0, sttTranscriptLog.length)
  try {
    localStorage.removeItem(TRANSCRIPT_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

function localTimeLabel(d: Date): string {
  return d.toTimeString().slice(0, 8) // "HH:MM:SS"
}

function pushTranscriptEntry(entry: SttTranscriptEntry) {
  sttTranscriptLog.push(entry)
  while (sttTranscriptLog.length > TRANSCRIPT_MAX) sttTranscriptLog.shift()
  persistTranscriptLog()
}

/** One in-progress partial entry per lang — updated in place instead of appending a new entry
 *  every partial tick (same convention DebugStt.vue used to keep locally, round-19). */
const partialEntryByLang: Partial<Record<SttLang, SttTranscriptEntry>> = {}

function recordPartial(text: string, lang: SttLang) {
  const now = Date.now()
  const existing = partialEntryByLang[lang]
  if (existing && sttTranscriptLog.includes(existing)) {
    existing.text = text
    existing.ts = now
    existing.time = localTimeLabel(new Date(now))
    persistTranscriptLog()
    return
  }
  const entry: SttTranscriptEntry = { id: ++transcriptId, ts: now, time: localTimeLabel(new Date(now)), lang, text, kind: 'partial' }
  partialEntryByLang[lang] = entry
  pushTranscriptEntry(entry)
}

/** Tracks recently-finalized transcript entries by the SERVER's final.ts (seconds), so a command
 *  classification that arrives shortly after (grammar synchronously, semantic after its grace
 *  delay) can find its way back to the right entry — see attachCommandToRecentFinal and
 *  recordSpeakerMatchForCommand below. Same pairing convention as CLAIM_PAIR_WINDOW_MS. */
interface PendingFinalRef {
  entry: SttTranscriptEntry
  finalTs: number
  recordedAt: number
}
const pendingFinalRefs: PendingFinalRef[] = []
const PENDING_FINAL_TTL_MS = 15_000

function prunePendingFinalRefs() {
  const now = Date.now()
  while (pendingFinalRefs.length && now - pendingFinalRefs[0].recordedAt > PENDING_FINAL_TTL_MS) pendingFinalRefs.shift()
}

function recordFinal(final: SttFinalEvent) {
  const now = Date.now()
  const stalePartial = partialEntryByLang[final.lang]
  let entry: SttTranscriptEntry
  if (stalePartial && sttTranscriptLog.includes(stalePartial)) {
    stalePartial.text = final.text
    stalePartial.kind = 'final'
    stalePartial.ts = now
    stalePartial.time = localTimeLabel(new Date(now))
    stalePartial.durationSec = final.durationSec ?? null
    entry = stalePartial
    delete partialEntryByLang[final.lang]
    persistTranscriptLog()
  } else {
    entry = { id: ++transcriptId, ts: now, time: localTimeLabel(new Date(now)), lang: final.lang, text: final.text, kind: 'final', durationSec: final.durationSec ?? null }
    pushTranscriptEntry(entry)
  }
  prunePendingFinalRefs()
  pendingFinalRefs.push({ entry, finalTs: final.ts, recordedAt: now })
}

function attachCommandToRecentFinal(cmd: SttCommandEvent) {
  prunePendingFinalRefs()
  let best: PendingFinalRef | null = null
  let bestDiff = Infinity
  for (const ref of pendingFinalRefs) {
    if (ref.entry.command) continue // first classification wins — never overwrite
    const diff = Math.abs(cmd.ts - ref.finalTs) * 1000
    if (diff < bestDiff) {
      bestDiff = diff
      best = ref
    }
  }
  if (!best || bestDiff > CLAIM_PAIR_WINDOW_MS) return
  best.entry.command = { command: cmd.command, color: cmd.color, source: cmd.source === 'semantic' ? 'semantic' : 'grammar', confidence: cmd.confidence }
  persistTranscriptLog()
}

/** Called by cornerClaim.ts once it resolves a corner_claim's speaker match (any outcome that
 *  actually attempted a match) — logs the outcome onto the same transcript entry the command was
 *  recorded against, so the debug log shows who (if anyone) was matched without needing the live
 *  Speaker match panel open at the time it happened. */
export function recordSpeakerMatchForCommand(
  cmd: SttCommandEvent,
  match: { playerId: number | null; displayName?: string | null; confidencePct: number | null; outcome: string; scope?: string | null },
) {
  prunePendingFinalRefs()
  let best: PendingFinalRef | null = null
  let bestDiff = Infinity
  for (const ref of pendingFinalRefs) {
    const diff = Math.abs(cmd.ts - ref.finalTs) * 1000
    if (diff < bestDiff) {
      bestDiff = diff
      best = ref
    }
  }
  if (!best || bestDiff > CLAIM_PAIR_WINDOW_MS) return
  best.entry.speakerMatch = match
  persistTranscriptLog()
}

// Round-17 (2026-08-31, live-testing fix): bumped on every startEnrollment so a delayed
// stopEnrollment(graceMs) from a PREVIOUS session can tell it's stale and must not clobber a
// session that has since restarted — see stopEnrollment's generation check below.
let enrollGeneration = 0

export function startEnrollment(playerId: number, lang: SttLang) {
  enrollGeneration += 1
  stt.enrollPlayerId = playerId
  stt.enrollCount = 0
  stt.enrollLang = lang
}

/**
 * Round-17 fix: clicking "Stop" used to null enrollPlayerId synchronously, which dropped
 * whatever utterance was still mid-recognition at that instant — kmb-stt's "final" event for
 * it arrives a moment later, by which point forwardSample() saw enrolling=false and posted it
 * unlabeled (source "kiosk", subject to the passive prune) instead of as enrollment. `graceMs`
 * keeps enrollPlayerId set for a short window after Stop so that trailing final still lands
 * labeled and permanent, matching "everything captured up to the point of stopping is kept."
 */
export function stopEnrollment(graceMs = 0) {
  if (graceMs <= 0) {
    stt.enrollPlayerId = null
    stt.enrollLang = null
    return
  }
  const gen = enrollGeneration
  setTimeout(() => {
    if (gen === enrollGeneration) {
      stt.enrollPlayerId = null
      stt.enrollLang = null
    }
  }, graceMs)
}

// Passive corpus throttle (round-16, 2026-08-31: loosened from 30s to 2s per user request —
// "full transcript/diarization logging" means every utterance worth hearing should land in the
// corpus, not one sample a match). Every final with actual text now forwards, whether or not it
// carries a fingerprint or was a classified command — diarization (spk matching, later) needs
// the FULL transcript to work from, not just the moments a command happened to fire. The 2s
// floor is purely a "don't double-post the same final twice" guard, not a sampling decision.
// Retention (last-100 prune, see server/app/routers/voice.py) is what keeps this from growing
// unbounded, not the throttle. Enrollment samples are never throttled.
const PASSIVE_MIN_INTERVAL_MS = 2_000
let lastPassivePostAt = 0

function forwardSample(final: SttFinalEvent, command: string | null) {
  const enrolling = stt.enrollPlayerId != null
  if (!final.text.trim()) return
  // Item 1: while enrolling, a final in the OTHER language is the parallel es/en decoder's
  // garbage transcription of the same utterance the expected-lang decoder already handled
  // (or is about to) — never a second thing the trainee said. Drop it entirely: no post, no
  // enrollCount bump. Passive (non-enrolling) forwarding is unaffected — it's not tied to any
  // "expected" language.
  if (enrolling && stt.enrollLang != null && final.lang !== stt.enrollLang) return
  if (!enrolling) {
    const now = Date.now()
    if (now - lastPassivePostAt < PASSIVE_MIN_INTERVAL_MS) return
    lastPassivePostAt = now
  }
  postVoiceSample({
    lang: final.lang,
    text: final.text,
    command,
    playerId: stt.enrollPlayerId,
    spkVector: final.spk ?? null,
    spkModelVersion: final.spkModelVersion ?? null,
    audioB64: final.audioB64 ?? null,
    source: enrolling ? 'enrollment' : 'kiosk',
  })
    .then(() => {
      if (enrolling) stt.enrollCount += 1
    })
    .catch(() => {
      /* corpus collection is best-effort — never surfaces to the UI */
    })
}

// --------------------------------------------------------------------------------------------
// Tier-2 semantic intent fallback (round-24, 2026-08-31 — DESIGN.md §11 "Semantic voice
// matching", semanticIntent.ts). The grammar path (kmb_stt.py) and the free-decode 'final' path
// run as two independent recognizers over the SAME audio, so they don't finalize in lockstep —
// a 'command' event for utterance X can arrive slightly before OR after the 'final' event for
// the same utterance. So: on every 'final' with text, wait SEMANTIC_FALLBACK_GRACE_MS for a
// same-utterance grammar command to show up (checked by timestamp proximity, same pairing style
// as CLAIM_PAIR_WINDOW_MS above); if none did, hand the transcript to the semantic classifier.
// Grammar ALWAYS wins when both would fire — this only ever runs on utterances the grammar path
// left unclassified.
// --------------------------------------------------------------------------------------------
const SEMANTIC_FALLBACK_GRACE_MS = 400
/** How close (ms) a grammar command's ts must be to a final's ts to count as "that final's own
 *  grammar result already fired" — slightly wider than the grace delay itself to absorb network/
 *  event-loop jitter between the two WS messages. */
const SEMANTIC_PAIR_WINDOW_MS = SEMANTIC_FALLBACK_GRACE_MS + 250

function maybeRunSemanticFallback(final: SttFinalEvent) {
  if (!settings.semanticFallbackEnabled) return
  if (!final.text.trim()) return
  const finalTs = final.ts
  setTimeout(() => {
    const cmd = stt.lastCommand
    if (cmd && !cmd.source && Math.abs(cmd.ts - finalTs) * 1000 <= SEMANTIC_PAIR_WINDOW_MS) return // grammar already classified this utterance
    void runSemanticFallback(final)
  }, SEMANTIC_FALLBACK_GRACE_MS)
}

async function runSemanticFallback(final: SttFinalEvent) {
  const { classifySemanticIntent } = await import('./semanticIntent')
  const result = await classifySemanticIntent(final.text)
  if (!result) return // model not loaded/ready — no opinion, silently skip (never blocks anything)
  // Round-24: "track everything" (DESIGN.md §16) — every attempt ships, accepted or rejected,
  // a rejection is exactly as important to see as an acceptance (same convention as
  // guard_rejected above / cornerClaim.ts's trackCornerClaim).
  shipEvent('semantic-intent', {
    transcript: result.transcript,
    lang: final.lang,
    top: result.top,
    runnerUp: result.runnerUp,
    margin: Math.round(result.margin * 1000) / 1000,
    accepted: result.accepted,
    rejectReason: result.rejectReason,
    command: result.command?.command,
    color: result.command?.color,
    answer: result.command?.answer,
    latencyMs: result.latencyMs,
  })
  if (!result.accepted || !result.command) return
  const cmd: SttCommandEvent = {
    type: 'command',
    command: result.command.command,
    raw: result.transcript,
    lang: final.lang,
    confidence: Math.round(result.top.score * 1000) / 1000,
    score: null,
    color: result.command.color,
    answer: result.command.answer,
    source: 'semantic',
    ts: Date.now() / 1000,
  }
  dispatchCommand(cmd, final.ts)
}

// Round-25 (2026-08-31): the single dispatch path for BOTH the grammar 'command' branch below and
// the semantic fallback above — so the double-fire guard lives in exactly one place. Normally
// maybeRunSemanticFallback's grace-window check already keeps the two paths from ever firing for
// the same utterance (grammar arriving during the wait aborts the semantic attempt entirely), but
// if grammar's classification is unusually slow and only arrives AFTER that grace window elapsed
// (semantic already fired), this is the second line of defense: never dispatch two commands for
// the same final. `pairedFinalTs` is the ts (SttFinalEvent.ts units — seconds) of the final this
// command belongs to, when known; null skips the dedupe check entirely (nothing to compare).
let lastDispatchedFinalTs: number | null = null

function dispatchCommand(cmd: SttCommandEvent, pairedFinalTs: number | null) {
  stt.lastCommand = cmd
  attachCommandToRecentFinal(cmd)
  const isDuplicate =
    pairedFinalTs != null &&
    lastDispatchedFinalTs != null &&
    Math.abs(pairedFinalTs - lastDispatchedFinalTs) * 1000 <= SEMANTIC_PAIR_WINDOW_MS
  if (isDuplicate) return
  if (pairedFinalTs != null) lastDispatchedFinalTs = pairedFinalTs
  for (const fn of commandListeners) {
    try {
      fn(cmd)
    } catch {
      /* one bad listener must not break the rest */
    }
  }
}

const WS_URL = 'ws://127.0.0.1:9224'
const BACKOFF_START_MS = 1000
const BACKOFF_MAX_MS = 15000

let ws: WebSocket | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let backoff = BACKOFF_START_MS
let started = false

function handleMessage(raw: string) {
  let ev: { type?: string }
  try {
    ev = JSON.parse(raw)
  } catch {
    return
  }
  if (ev.type === 'level') {
    stt.level = (ev as { rms: number }).rms
  } else if (ev.type === 'partial') {
    const p = ev as { text: string; lang: SttLang; ts: number }
    recordPartial(p.text, p.lang)
  } else if (ev.type === 'final') {
    const final = ev as SttFinalEvent
    stt.lastFinal = final
    recordFinal(final)
    forwardSample(final, null)
    maybeRunSemanticFallback(final)
  } else if (ev.type === 'command') {
    const cmd = ev as SttCommandEvent
    const f = stt.lastFinal
    // Same proximity check maybeRunSemanticFallback uses (in reverse) to decide "this command
    // belongs to that final" — used both to correlate the transcript entry and to feed the
    // double-fire dedupe in dispatchCommand.
    const pairedFinalTs = f && Math.abs(cmd.ts - f.ts) * 1000 <= SEMANTIC_PAIR_WINDOW_MS ? f.ts : null
    dispatchCommand(cmd, pairedFinalTs)
    // A command utterance is corpus gold — forward the paired final again with its label.
    if (f && Math.abs(Date.now() - f.ts * 1000) < CLAIM_PAIR_WINDOW_MS) forwardSample(f, cmd.command)
  } else if (ev.type === 'guard_rejected') {
    // Round-22 (2026-08-31): "track everything" (DESIGN.md § Observability) — kmb_stt.py's
    // precision gate (AGGRESSIVE_MAX_TOKENS/MIN_COVERAGE/MIN_CONFIDENCE) rejected an utterance
    // that otherwise looked like a command; false negatives here are BY DESIGN (precision-first),
    // but they still need to be visible in the eventstream to debug "why didn't my claim fire" —
    // shipped app-wide (this is the one shared stt.ts client) via the same /ifl relay every other
    // track event uses.
    const gr = ev as { wouldBe: string; reason: string; raw: string; lang: SttLang; confidence: number }
    shipEvent('guard-rejected', { wouldBe: gr.wouldBe, reason: gr.reason, raw: gr.raw, lang: gr.lang, confidence: gr.confidence })
  }
}

function connect() {
  if (!settings.voiceCommandsEnabled) return
  ws = new WebSocket(WS_URL)
  ws.addEventListener('open', () => {
    backoff = BACKOFF_START_MS
    stt.connected = true
  })
  ws.addEventListener('message', (evt) => handleMessage(evt.data as string))
  ws.addEventListener('close', () => {
    stt.connected = false
    stt.level = 0
    ws = null
    if (settings.voiceCommandsEnabled) {
      retryTimer = setTimeout(connect, backoff)
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
    }
  })
  ws.addEventListener('error', () => ws?.close())
}

/** How long after app boot the semantic model's background load is kicked off — deliberately
 *  deferred (not "immediately in initStt()") so the ~150MB fetch + WASM instantiation never
 *  competes with first paint/first interaction, even though the fetch/instantiate work is
 *  already async. Round-24, 2026-08-31 — DESIGN.md §11 "lazy + non-blocking" requirement. */
const SEMANTIC_MODEL_PRELOAD_DELAY_MS = 2000

/** Called once from main.ts. Reacts to the settings toggle for the app's whole lifetime. */
export function initStt() {
  if (started) return
  started = true
  if (settings.voiceCommandsEnabled) connect()
  watch(
    () => settings.voiceCommandsEnabled,
    (on) => {
      if (on && !ws) connect()
      if (!on) {
        if (retryTimer) clearTimeout(retryTimer)
        ws?.close()
        ws = null
        stt.connected = false
        stt.level = 0
      }
    },
  )

  // Round-24: background-load the semantic model once, on boot, and again if the setting is
  // switched back on later — never blocking, never re-loading once ready (ensureSemanticModelLoaded
  // memoizes; the dynamic import() itself is also memoized by the module system after the first
  // call, so this is cheap on every subsequent toggle). Runs independently of
  // settings.voiceCommandsEnabled: the two toggles are separate (a user could flip semantic on
  // before voice commands, or leave voice on with semantic off), and preloading costs nothing but
  // bandwidth/CPU the app wasn't otherwise using.
  function preloadSemanticModel() {
    void import('./semanticIntent').then((m) => m.ensureSemanticModelLoaded())
  }
  if (settings.semanticFallbackEnabled) {
    setTimeout(preloadSemanticModel, SEMANTIC_MODEL_PRELOAD_DELAY_MS)
  }
  watch(
    () => settings.semanticFallbackEnabled,
    (on) => {
      if (on) preloadSemanticModel()
    },
  )
}
